import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProdAuth } from '../../contexts/ProdAuthContext';

const ACCENT = '#4A7C59';

interface Farmer {
  id: string;
  memberId: string;
  name: string;
  ward: string;
}

interface ArrivedTrip {
  id: string;
  tripDate: string;
  driverName: string | null;
  cherryKgLoaded: string | null;
  cherryKgReceived: string | null;
  vehicleReg: string | null;
  centreName: string | null;
  centreId: string | null;
}

export const DeliveryForm: React.FC = () => {
  const { user } = useProdAuth();
  const navigate = useNavigate();

  const isHub = user && ['admin', 'super_admin'].includes(user.role);

  // Hub (Florence) state — trip-based intake
  const [arrivedTrips, setArrivedTrips] = useState<ArrivedTrip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<ArrivedTrip | null>(null);

  // Field state — farmer-based
  const [farmers, setFarmers] = useState<Farmer[]>([]);
  const [farmerSearch, setFarmerSearch] = useState('');
  const [showFarmerDropdown, setShowFarmerDropdown] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState<Farmer | null>(null);

  const [form, setForm] = useState({
    deliveryDate: new Date().toISOString().split('T')[0],
    cherryKg: '',
    cherryQuality: 'A',
    pricePerKg: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Load arrived trips for hub users
  useEffect(() => {
    if (!isHub) return;
    setTripsLoading(true);
    fetch('/api/production/deliveries?resource=arrived-trips', { credentials: 'include' })
      .then(r => r.json())
      .then(d => setArrivedTrips(d.trips || []))
      .catch(() => setArrivedTrips([]))
      .finally(() => setTripsLoading(false));
  }, [isHub]);

  // Farmer search for field users
  useEffect(() => {
    if (isHub) return;
    if (farmerSearch.length < 2) { setFarmers([]); return; }
    const timeout = setTimeout(() => {
      const params = new URLSearchParams({ search: farmerSearch, limit: '10' });
      fetch(`/api/production/farmers?${params}`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => setFarmers(d.farmers || []))
        .catch(() => setFarmers([]));
    }, 300);
    return () => clearTimeout(timeout);
  }, [farmerSearch, isHub]);

  // When a trip is selected, pre-fill date and kg
  const selectTrip = (trip: ArrivedTrip) => {
    setSelectedTrip(trip);
    setForm(f => ({
      ...f,
      deliveryDate: trip.tripDate,
      cherryKg: trip.cherryKgReceived || trip.cherryKgLoaded || '',
    }));
  };

  const totalAmount = form.cherryKg && form.pricePerKg
    ? (parseFloat(form.cherryKg) * parseFloat(form.pricePerKg)).toFixed(2)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isHub && !selectedTrip) {
      setError('Please select an arrived trip');
      return;
    }
    if (!form.cherryKg || parseFloat(form.cherryKg) <= 0) {
      setError('Cherry weight must be greater than 0');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body: any = {
        deliveryDate: form.deliveryDate,
        cherryKg: form.cherryKg,
        cherryQuality: form.cherryQuality,
        pricePerKg: form.pricePerKg || null,
        notes: form.notes,
      };
      if (isHub && selectedTrip) {
        body.tripId = selectedTrip.id;
        body.collectionCentreId = selectedTrip.centreId || null;
      } else {
        body.farmerId = selectedFarmer?.id || null;
      }

      const res = await fetch('/api/production/deliveries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Failed to record delivery');
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSelectedTrip(null);
        setSelectedFarmer(null);
        setFarmerSearch('');
        setForm(f => ({ ...f, cherryKg: '', notes: '', pricePerKg: '' }));
        // Refresh trip list so used trip is still shown (or remove from list if desired)
      }, 1500);
    } catch {
      setError('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const qualityColors: Record<string, string> = {
    A: 'border-green-400 bg-green-50 text-green-700',
    B: 'border-yellow-400 bg-yellow-50 text-yellow-700',
    C: 'border-red-400 bg-red-50 text-red-700',
  };

  const inputCls = "w-full px-4 py-3 border border-[#E8E6E2] rounded-lg bg-white text-sm text-[#1A1A1A] focus:outline-none";
  const foc = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`);
  const blu = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    (e.target.style.boxShadow = '');

  return (
    <div className="max-w-lg">
      <div className="mb-6">
        <button
          onClick={() => navigate('/production/deliveries')}
          className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A] flex items-center gap-1 mb-3"
        >
          ← Back to deliveries
        </button>
        <h2 className="text-2xl font-bold text-[#1A1A1A]">
          {isHub ? 'Receive cherry at hub' : 'Record delivery'}
        </h2>
        <p className="text-sm text-[#6B6B6B] mt-0.5">
          {isHub ? 'Log cherry received from a transport trip' : 'Log cherry intake from a farmer'}
        </p>
      </div>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm font-medium">
          ✓ Delivery recorded successfully
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── Hub: trip selector ────────────────────────────────────────── */}
        {isHub && (
          <div>
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              Trip from station <span className="text-red-500">*</span>
            </label>

            {selectedTrip ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">{selectedTrip.centreName || 'Unknown station'}</p>
                    <div className="flex gap-3 mt-1 text-xs text-[#6B6B6B]">
                      <span>{new Date(selectedTrip.tripDate).toLocaleDateString('en-KE', { weekday: 'short', day: '2-digit', month: 'short' })}</span>
                      {selectedTrip.vehicleReg && <span>{selectedTrip.vehicleReg}</span>}
                      {selectedTrip.driverName && <span>{selectedTrip.driverName}</span>}
                    </div>
                    <div className="flex gap-3 mt-1 text-xs">
                      {selectedTrip.cherryKgLoaded && (
                        <span className="text-[#6B6B6B]">{parseFloat(selectedTrip.cherryKgLoaded).toLocaleString()} kg loaded</span>
                      )}
                      {selectedTrip.cherryKgReceived && (
                        <span className="text-green-700 font-medium">{parseFloat(selectedTrip.cherryKgReceived).toLocaleString()} kg expected</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedTrip(null); setForm(f => ({ ...f, cherryKg: '' })); }}
                    className="text-xs text-[#6B6B6B] hover:text-red-600 ml-2"
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : tripsLoading ? (
              <div className="py-6 text-center text-sm text-[#A3A3A3] border border-[#E8E6E2] rounded-xl">Loading trips...</div>
            ) : arrivedTrips.length === 0 ? (
              <div className="py-6 text-center border border-dashed border-[#E8E6E2] rounded-xl">
                <p className="text-sm text-[#A3A3A3]">No arrived trips waiting to be received</p>
                <a href="/production/trips" className="text-xs font-medium mt-1 inline-block" style={{ color: ACCENT }}>
                  View trips →
                </a>
              </div>
            ) : (
              <div className="border border-[#E8E6E2] rounded-xl overflow-hidden">
                {arrivedTrips.map((trip, i) => (
                  <button
                    key={trip.id}
                    type="button"
                    onClick={() => selectTrip(trip)}
                    className={`w-full text-left px-4 py-3 hover:bg-[#F0EDE8] transition-colors border-b border-[#E8E6E2] last:border-0 ${i % 2 !== 0 ? 'bg-[#FAFAF8]' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A]">{trip.centreName || 'Unknown station'}</p>
                        <div className="flex gap-3 mt-0.5 text-xs text-[#6B6B6B]">
                          <span>{new Date(trip.tripDate).toLocaleDateString('en-KE', { weekday: 'short', day: '2-digit', month: 'short' })}</span>
                          {trip.vehicleReg && <span>{trip.vehicleReg}</span>}
                          {trip.driverName && <span>{trip.driverName}</span>}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        {trip.cherryKgReceived ? (
                          <p className="text-sm font-bold" style={{ color: ACCENT }}>
                            {parseFloat(trip.cherryKgReceived).toLocaleString()} kg
                          </p>
                        ) : trip.cherryKgLoaded ? (
                          <p className="text-sm text-[#6B6B6B]">{parseFloat(trip.cherryKgLoaded).toLocaleString()} kg</p>
                        ) : null}
                        <p className="text-[10px] text-[#A3A3A3]">Select →</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Field: farmer lookup ─────────────────────────────────────── */}
        {!isHub && (
          <div className="relative">
            <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
              Farmer (optional)
            </label>
            {selectedFarmer ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <span className="font-medium text-[#1A1A1A]">{selectedFarmer.memberId}</span>
                  {selectedFarmer.name && <span className="text-[#6B6B6B] ml-2">{selectedFarmer.name}</span>}
                  {selectedFarmer.ward && <span className="text-[#A3A3A3] ml-1 text-xs">· {selectedFarmer.ward}</span>}
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedFarmer(null); setFarmerSearch(''); }}
                  className="text-xs text-[#6B6B6B] hover:text-red-600"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={farmerSearch}
                  onChange={e => { setFarmerSearch(e.target.value); setShowFarmerDropdown(true); }}
                  onFocus={() => setShowFarmerDropdown(true)}
                  onBlur={() => setTimeout(() => setShowFarmerDropdown(false), 150)}
                  placeholder="Search member ID or name..."
                  className={inputCls}
                />
                {showFarmerDropdown && farmers.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-[#E8E6E2] rounded-lg shadow-lg overflow-hidden">
                    {farmers.map(f => (
                      <button
                        key={f.id}
                        type="button"
                        onMouseDown={() => { setSelectedFarmer(f); setFarmerSearch(''); setShowFarmerDropdown(false); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#F0EDE8] text-sm"
                      >
                        <span className="font-medium text-[#1A1A1A]">{f.memberId}</span>
                        {f.name && <span className="text-[#6B6B6B] ml-2">{f.name}</span>}
                        {f.ward && <span className="text-[#A3A3A3] ml-1 text-xs">· {f.ward}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Date */}
        <div>
          <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Date</label>
          <input type="date" value={form.deliveryDate}
            onChange={e => setForm(f => ({ ...f, deliveryDate: e.target.value }))}
            required className={inputCls} onFocus={foc} onBlur={blu} />
        </div>

        {/* Weight */}
        <div>
          <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
            Cherry weight (kg) <span className="text-red-500">*</span>
          </label>
          <input type="number" value={form.cherryKg}
            onChange={e => setForm(f => ({ ...f, cherryKg: e.target.value }))}
            placeholder="0.00" min="0.1" step="0.1" required
            className={inputCls} onFocus={foc} onBlur={blu} />
          {isHub && selectedTrip?.cherryKgReceived && form.cherryKg &&
            parseFloat(form.cherryKg) !== parseFloat(selectedTrip.cherryKgReceived) && (
            <p className="mt-1 text-xs text-amber-600">
              Differs from trip record ({parseFloat(selectedTrip.cherryKgReceived).toLocaleString()} kg expected)
            </p>
          )}
        </div>

        {/* Quality */}
        <div>
          <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-2">Quality grade</label>
          <div className="flex gap-3">
            {['A', 'B', 'C'].map(q => (
              <button key={q} type="button"
                onClick={() => setForm(f => ({ ...f, cherryQuality: q }))}
                className={`flex-1 py-3 rounded-lg border-2 font-bold text-sm transition-colors ${
                  form.cherryQuality === q
                    ? qualityColors[q]
                    : 'border-[#E8E6E2] text-[#A3A3A3] hover:border-[#C0BDB9]'
                }`}
              >
                Grade {q}
              </button>
            ))}
          </div>
        </div>

        {/* Price per kg */}
        <div>
          <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
            Price per kg (KES) <span className="text-[#A3A3A3]">optional</span>
          </label>
          <input type="number" value={form.pricePerKg}
            onChange={e => setForm(f => ({ ...f, pricePerKg: e.target.value }))}
            placeholder="0.00" min="0" step="0.01"
            className={inputCls} onFocus={foc} onBlur={blu} />
          {totalAmount && (
            <p className="mt-1 text-xs text-[#6B6B6B]">
              Total: <strong className="text-[#1A1A1A]">KES {parseFloat(totalAmount).toLocaleString()}</strong>
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">Notes</label>
          <textarea value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={3} placeholder="Any additional notes..."
            className="w-full px-4 py-3 border border-[#E8E6E2] rounded-lg bg-white text-sm text-[#1A1A1A] resize-none focus:outline-none"
            onFocus={foc} onBlur={blu} />
        </div>

        <button
          type="submit"
          disabled={submitting || (isHub && !selectedTrip)}
          className="w-full py-3.5 text-white font-medium rounded-lg transition-opacity disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          {submitting ? 'Recording...' : isHub ? 'Confirm receipt' : 'Record delivery'}
        </button>
      </form>
    </div>
  );
};
